import type { Call, InvocationsSignerDetails, TypedData } from "starknet";
import type { PrivacyStatus, PrivacySubmission } from "../chains/starknet/privacy/session";
import type { SocialRecoveryCredential } from "../recovery/SocialRecoveryCredential";

export const VAULT_READY = "cavos-vault:ready";
export const VAULT_HELLO = "cavos-vault:hello";
export const POPUP_READY = "cavos-vault:popup-ready";
export const POPUP_RESULT = "cavos-vault:popup-result";

export type VaultChain = "solana" | "stellar" | "starknet";
export type RecoveryMode = "enclave" | "passkey" | "none";

export interface ConnectParams {
  chain: VaultChain;
  network: string;
  environment?: "development" | "production";
  appSalt: string;
  userId: string;
  userName?: string;
  recovery: RecoveryMode;
  credential?: SocialRecoveryCredential;
  authToken?: string | null;
}

export interface ConnectResult {
  handle: string;
  address: string;
  /**
   * Null when this browser holds no key for the account yet. Ed25519 raw bytes
   * on Solana and Stellar; the 32-byte stark public key on Starknet.
   */
  publicKey: Uint8Array | null;
  isNewAccount: boolean;
  /** A passkey the user added can restore this account on a new device. */
  passkey: boolean;
}

export interface VaultMethods {
  connect(params: ConnectParams): ConnectResult;
  signSolanaTransaction(params: { handle: string; message: Uint8Array }): Uint8Array;
  signStellarTransaction(params: { handle: string; xdr: string; networkPassphrase: string }): Uint8Array;
  signStellarAuthEntry(params: { handle: string; preimage: Uint8Array }): Uint8Array;
  signMessage(params: { handle: string; message: Uint8Array }): Uint8Array;
  signStarknetTypedData(params: { handle: string; typedData: TypedData; accountAddress: string }): string[];
  signStarknetInvoke(params: { handle: string; calls: Call[]; details: InvocationsSignerDetails }): string[];
  /** The pool, prover, discovery and relay come from the app's Cavos settings. The page names none of them. */
  privacyStatus(params: { handle: string; token?: string }): PrivacyStatus;
  /** Decimal string of base units. Only the sum leaves the vault, never a note. */
  privacyBalance(params: { handle: string; token: string }): string;
  privacyRegister(params: { handle: string; paymasterApiKey: string }): PrivacySubmission;
  privacyDeposit(params: { handle: string; token: string; amount: string; paymasterApiKey: string }): PrivacySubmission;
  privacyTransfer(params: {
    handle: string;
    token: string;
    recipient: string;
    amount: string;
    paymasterApiKey: string;
  }): PrivacySubmission;
  privacyWithdraw(params: {
    handle: string;
    token: string;
    recipient: string;
    amount: string;
    paymasterApiKey: string;
  }): PrivacySubmission;
  forget(params: { userId: string; appSalt: string }): void;
  /** Create a passkey that can restore the connected account on another device. */
  enrollPasskey(params: {
    userId: string;
    appSalt: string;
    userName?: string;
    environment?: "development" | "production";
    authToken?: string | null;
  }): void;
}

export type VaultMethod = keyof VaultMethods;
export type VaultParams<M extends VaultMethod> = Parameters<VaultMethods[M]>[0];
export type VaultResult<M extends VaultMethod> = ReturnType<VaultMethods[M]>;

export type VaultRequest = { [M in VaultMethod]: { id: number; method: M; params: VaultParams<M> } }[VaultMethod];

export type VaultReply =
  | { id: number; ok: true; result: unknown }
  | { id: number; ok: false; error: string };

export interface VaultEvent {
  event: "show" | "hide";
}

/** The Cavos window is the fallback for passkeys some browsers refuse inside a cross-site frame. */
export type PopupIntent = { kind: "passkey"; userId: string; userName: string; create: boolean; credentialId?: Uint8Array };

export type PopupResult = { secret: Uint8Array; credentialId?: Uint8Array } | { error: string };
